import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { LogIn, Eye, EyeOff, LogOut, Home, Receipt, FolderKanban, Upload, CheckCircle2, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import ThemeSwitch from '../../components/ThemeSwitch';

const api = axios.create({ baseURL: '/api/portal' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('portal_token');
  if (t) c.headers.Authorization = 'Bearer ' + t;
  return c;
});

const money = (v) => '$' + Number(v || 0).toLocaleString('es-AR');
const fecha = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—');
const ESTADOS = {
  pendiente: { label: 'Pendiente', color: '#f59e0b' }, vencido: { label: 'Vencido', color: '#ef4444' },
  informado: { label: 'Pago informado', color: '#0ea5e9' }, pagado: { label: 'Pagado', color: '#22c55e' },
};

/* ---------------- Login ---------------- */
function Login({ onEntrar }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [ver, setVer] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [olvide, setOlvide] = useState(false);

  const entrar = async (e) => {
    e.preventDefault();
    setCargando(true);
    try {
      const r = await api.post('/login', form);
      localStorage.setItem('portal_token', r.data.token);
      onEntrar(r.data.usuario);
    } catch (err) { toast.error(err.response?.data?.error || 'No pudimos entrar'); }
    finally { setCargando(false); }
  };
  const recuperar = async (e) => {
    e.preventDefault();
    await api.post('/olvide', { email: form.email }).catch(() => {});
    toast.success('Si el email existe, te enviamos las instrucciones', { duration: 6000 });
    setOlvide(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'var(--bg-primary)' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '36px 32px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/icon-192.png?v=2" alt="" style={{ width: 54, height: 54, borderRadius: 12 }} />
          <div style={{ fontSize: 18, fontWeight: 800, marginTop: 10 }}>Portal de clientes</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>U.dream</div>
        </div>
        {olvide ? (
          <form onSubmit={recuperar}>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>Ponés tu email y te mandamos un enlace para crear una contraseña nueva.</p>
            <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required autoFocus /></div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} type="submit">Enviar instrucciones</button>
            <button type="button" className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={() => setOlvide(false)}>Volver</button>
          </form>
        ) : (
          <form onSubmit={entrar}>
            <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required autoFocus /></div>
            <div className="form-group"><label>Contraseña</label>
              <div style={{ position: 'relative' }}>
                <input type={ver ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required style={{ paddingRight: 42 }} />
                <button type="button" onClick={() => setVer(!ver)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  {ver ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} disabled={cargando} type="submit">
              <LogIn size={16} /> {cargando ? 'Entrando...' : 'Entrar'}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 10 }} onClick={() => setOlvide(true)}>Olvidé mi contraseña</button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------------- Recuperar contraseña ---------------- */
function Recuperar() {
  const [params] = useSearchParams();
  const [pass, setPass] = useState({ a: '', b: '' });
  const [listo, setListo] = useState(false);
  const navigate = useNavigate();
  const guardar = async (e) => {
    e.preventDefault();
    if (pass.a !== pass.b) return toast.error('Las contraseñas no coinciden');
    try { await api.post('/recuperar', { token: params.get('token'), password: pass.a }); setListo(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 400, padding: 32, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16 }}>
        <h2 style={{ marginBottom: 14 }}>Nueva contraseña</h2>
        {listo ? (<>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Listo, ya podés entrar con tu contraseña nueva.</p>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/')}>Ir al portal</button>
        </>) : (
          <form onSubmit={guardar}>
            <div className="form-group"><label>Contraseña (mínimo 8 caracteres)</label><input type="password" value={pass.a} onChange={e => setPass({ ...pass, a: e.target.value })} required minLength={8} autoFocus /></div>
            <div className="form-group"><label>Repetir contraseña</label><input type="password" value={pass.b} onChange={e => setPass({ ...pass, b: e.target.value })} required /></div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} type="submit">Guardar</button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------------- Inicio ---------------- */
function Inicio({ onPagar }) {
  const [d, setD] = useState(null);
  const cargar = useCallback(() => { api.get('/inicio').then(r => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { cargar(); }, [cargar]);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  const card = { marginBottom: 18 };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Hola{d.usuario.nombre ? ', ' + d.usuario.nombre.split(' ')[0] : ''}</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>{d.usuario.cliente}</p>

      {d.usuario.ve_importes && d.cobros.length > 0 && (
        <div className="card" style={card}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Pagos pendientes</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{d.cobros.map(c => {
              const e = ESTADOS[c.estado] || {};
              return (
                <tr key={c.id}>
                  <td style={td}><div style={{ fontWeight: 600 }}>{c.concepto}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Vence {fecha(c.vencimiento)} · <span style={{ color: e.color }}>{e.label}</span></div></td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(c.monto)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {c.estado !== 'informado' && <button className="btn btn-primary btn-sm" onClick={() => onPagar(c)}>Ya pagué</button>}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Tus servicios</h3>
          {d.contratos.length ? d.contratos.map(c => (
            <div key={c.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border)', fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <div><div style={{ fontWeight: 600 }}>{c.nombre}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {c.tipo === 'abono' ? 'Abono mensual' : c.tipo === 'anual' ? `Renovación anual · vence ${fecha(c.proximo_vencimiento)}` : 'Proyecto'}
                  {d.usuario.es_agencia && c.cliente !== d.usuario.cliente ? ` · ${c.cliente}` : ''}
                </div></div>
              {d.usuario.ve_importes && c.monto && <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{money(c.monto)}</div>}
            </div>
          )) : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Todavía no tenés servicios cargados.</p>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Últimas novedades</h3>
          {d.avances.length ? d.avances.map(a => (
            <div key={a.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
              <div style={{ fontWeight: 600 }}>{a.titulo}</div>
              {a.detalle && <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{a.detalle}</div>}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{a.proyecto} · {fecha(a.created_at)}</div>
            </div>
          )) : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Cuando avancemos con tus proyectos, lo vas a ver acá.</p>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Facturas ---------------- */
function Facturas({ onPagar }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/facturas').then(r => setRows(r.data)).catch(() => {}); }, []);
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '8px' };
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 18 }}>Facturas y pagos</h1>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-container">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Concepto</th><th style={th}>Vence</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th><th style={th}>Factura</th><th style={th}></th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={6} style={{ ...td, color: 'var(--text-muted)' }}>Todavía no hay movimientos.</td></tr>
                : rows.map(c => {
                  const e = ESTADOS[c.estado] || {};
                  return (
                    <tr key={c.id}>
                      <td style={td}><div style={{ fontWeight: 600 }}>{c.concepto}</div>{c.cliente && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.cliente}</div>}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fecha(c.vencimiento)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                      <td style={{ ...td, color: e.color, fontWeight: 600 }}>{e.label}</td>
                      <td style={td}>{c.archivo_url ? <a href={c.archivo_url} target="_blank" rel="noopener noreferrer">Descargar</a> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={td}>{['pendiente', 'vencido'].includes(c.estado) && <button className="btn btn-primary btn-sm" onClick={() => onPagar(c)}>Ya pagué</button>}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Proyectos ---------------- */
function Proyectos() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/inicio').then(r => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  const ESTADO = { planificacion: 'Planificación', en_desarrollo: 'En desarrollo', revision: 'En revisión', publicacion: 'Publicación', terminado: 'Terminado' };
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 18 }}>Mis proyectos</h1>
      {d.proyectos.length ? d.proyectos.map(p => (
        <div key={p.id} className="card" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{p.nombre}</div>
            <span className="badge" style={{ background: 'var(--accent-glow)', color: 'var(--accent)' }}>{ESTADO[p.estado] || p.estado}</span>
          </div>
          {p.fecha_inicio && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Desde {fecha(p.fecha_inicio)}</div>}
          {d.avances.filter(a => a.proyecto === p.nombre).map(a => (
            <div key={a.id} style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', fontSize: 13 }}>
              <b>{a.titulo}</b> <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>· {fecha(a.created_at)}</span>
              {a.detalle && <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{a.detalle}</div>}
            </div>
          ))}
        </div>
      )) : <p style={{ color: 'var(--text-muted)' }}>Todavía no tenés proyectos cargados.</p>}
    </div>
  );
}

/* ---------------- Modal de pago ---------------- */
function ModalPago({ cobro, onClose }) {
  const [medios, setMedios] = useState([]);
  const [form, setForm] = useState({ fecha: new Date().toISOString().slice(0, 10), monto: cobro.monto, notas: '' });
  const [archivo, setArchivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { api.get('/medios-pago').then(r => setMedios(r.data)).catch(() => {}); }, []);

  const enviar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (archivo) fd.append('comprobante', archivo);
      await api.post(`/cobros/${cobro.id}/informar-pago`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('¡Gracias! Registramos tu aviso de pago');
      onClose(true);
    } catch { toast.error('No se pudo registrar'); }
    finally { setEnviando(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <h2>Informar un pago</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '6px 0 14px' }}>{cobro.concepto} · {money(cobro.monto)}</p>
        {medios.length > 0 && (
          <div style={{ background: 'var(--bg-hover)', borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Datos para transferir</div>
            {medios.map(m => <div key={m.id} style={{ marginBottom: 4 }}><b>{m.nombre}</b>{m.datos ? <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>{m.datos}</div> : null}</div>)}
          </div>
        )}
        <form onSubmit={enviar}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label>Fecha del pago</label><input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} required /></div>
            <div className="form-group"><label>Monto</label><input type="number" step="0.01" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} required /></div>
          </div>
          <div className="form-group"><label>Comprobante (opcional)</label>
            <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}><Upload size={14} /> {archivo ? archivo.name.slice(0, 26) : 'Subir archivo'}
              <input type="file" accept="image/*,.pdf" onChange={e => setArchivo(e.target.files[0])} style={{ display: 'none' }} /></label>
          </div>
          <div className="form-group"><label>Comentario (opcional)</label><input value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={enviando}><CheckCircle2 size={16} /> {enviando ? 'Enviando...' : 'Avisar el pago'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Cambiar contraseña ---------------- */
function ModalPassword({ onClose }) {
  const [f, setF] = useState({ actual: '', nueva: '', repetir: '' });
  const guardar = async (e) => {
    e.preventDefault();
    if (f.nueva !== f.repetir) return toast.error('Las contraseñas no coinciden');
    try { await api.put('/password', f); toast.success('Contraseña actualizada'); onClose(); }
    catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <h2>Cambiar contraseña</h2>
        <form onSubmit={guardar}>
          <div className="form-group"><label>Contraseña actual</label><input type="password" value={f.actual} onChange={e => setF({ ...f, actual: e.target.value })} required /></div>
          <div className="form-group"><label>Nueva (mínimo 8)</label><input type="password" minLength={8} value={f.nueva} onChange={e => setF({ ...f, nueva: e.target.value })} required /></div>
          <div className="form-group"><label>Repetir</label><input type="password" value={f.repetir} onChange={e => setF({ ...f, repetir: e.target.value })} required /></div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- App del portal ---------------- */
export default function PortalApp() {
  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [pagar, setPagar] = useState(null);
  const [cambiarPass, setCambiarPass] = useState(false);
  const [recargar, setRecargar] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem('portal_token')) { setCargando(false); return; }
    api.get('/inicio').then(r => setUsuario(r.data.usuario)).catch(() => localStorage.removeItem('portal_token')).finally(() => setCargando(false));
  }, []);

  const salir = () => { localStorage.removeItem('portal_token'); setUsuario(null); };

  if (window.location.pathname === '/recuperar') return <Recuperar />;
  if (cargando) return <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Cargando...</div>;
  if (!usuario) return <Login onEntrar={setUsuario} />;

  const link = ({ isActive }) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 10, fontSize: 14,
    fontWeight: isActive ? 600 : 500, color: isActive ? '#fff' : 'var(--text-secondary)',
    background: isActive ? 'var(--accent)' : 'transparent', textDecoration: 'none',
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <header style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <img src="/icon-192.png?v=2" alt="" style={{ width: 34, height: 34, borderRadius: 8 }} />
          <div style={{ marginRight: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.1 }}>{usuario.cliente}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Portal de clientes · U.dream</div>
          </div>
          <nav style={{ display: 'flex', gap: 4 }}>
            <NavLink to="/" end style={link}><Home size={16} /> Inicio</NavLink>
            {usuario.ve_importes && <NavLink to="/facturas" style={link}><Receipt size={16} /> Facturas</NavLink>}
            <NavLink to="/proyectos" style={link}><FolderKanban size={16} /> Proyectos</NavLink>
          </nav>
          <button className="btn btn-ghost btn-sm" onClick={() => setCambiarPass(true)} title="Cambiar contraseña"><Lock size={15} /></button>
          <button className="btn btn-ghost btn-sm" onClick={salir}><LogOut size={15} /> Salir</button>
        </div>
      </header>

      <main style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px 60px' }} key={recargar}>
        <Routes>
          <Route path="/" element={<Inicio onPagar={setPagar} />} />
          <Route path="/facturas" element={<Facturas onPagar={setPagar} />} />
          <Route path="/proyectos" element={<Proyectos />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <div style={{ maxWidth: 200, marginTop: 30 }}><ThemeSwitch /></div>
      </main>

      {pagar && <ModalPago cobro={pagar} onClose={(ok) => { setPagar(null); if (ok) setRecargar(r => r + 1); }} />}
      {cambiarPass && <ModalPassword onClose={() => setCambiarPass(false)} />}
    </div>
  );
}
